import Loading from '@justgiving/loading';
import React, { FunctionComponent, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FitnessApplicationActivityType,
  ManualFitnessEntrySettingsFragment,
} from '../../../types/__generated__';
import { useModalToggle } from '../../hooks/useModalToggle';
import usePageQuery, {
  buildQuery,
  UsePageQueryResult,
} from '../../hooks/usePageQuery';
import { LazyLoader } from '../LazyLoader/LazyLoader';
import SettingsModal from '../SettingsModal/SettingsModal';
import fragment from './ManualFitnessEntryFragment.gql';

const ManualFitnessEntryModal = React.lazy(() =>
  import('./ManualFitnessEntryModal/ManualFitnessEntryModal'),
);

const FRAGMENT = buildQuery(fragment);

export interface ManualFitnessEntryProps {
  opener: (props: { onOpen: () => void }) => ReactNode;
  onSubmit?: () => void;
  notes?: string;
}

const ManualFitnessEntry: FunctionComponent<ManualFitnessEntryProps> = ({
  opener,
  onSubmit: onSubmitCallback,
  notes,
}) => {
  const { t } = useTranslation('common', { useSuspense: false });
  const {
    isOpen: isModalOpen,
    setOpen: setModalOpen,
    ref,
    Portal,
  } = useModalToggle();
  const [isLoading, setIsLoading] = useState(false);

  const result = usePageQuery<ManualFitnessEntrySettingsFragment>(FRAGMENT, {
    notifyOnNetworkStatusChange: true,
  });

  if (!UsePageQueryResult.hasData(result)) {
    return null;
  }

  const { legacyId, type, slug, title, application } = result.data;
  const measurementSystem =
    application?.settings?.measurementSystem || undefined;
  const subscribedActivities = Object.values(FitnessApplicationActivityType);

  const openModal = async () => {
    setIsLoading(true);
    setModalOpen(true);
    await result.refetch();
    setIsLoading(false);
  };
  const closeModal = () => setModalOpen(false);
  const onSubmit = () => {
    // eslint-disable-next-line no-unused-expressions
    onSubmitCallback?.();
    closeModal();
  };

  return (
    <>
      {opener({ onOpen: openModal })}
      {isModalOpen && (
        <Portal
          background={true}
          focusLock={true}
          scrollLock={true}
          scrollIsolation={true}
        >
          <SettingsModal
            title={title}
            onCloseRequest={closeModal}
            aria-label={t('manualFitness.modalLabel')}
          >
            {isLoading ? (
              <Loading />
            ) : (
              <LazyLoader>
                <ManualFitnessEntryModal
                  pageId={legacyId}
                  pageType={type}
                  pageSlug={slug}
                  measurementSystem={measurementSystem}
                  subscribedActivities={subscribedActivities}
                  notes={notes}
                  onSubmit={onSubmit}
                  onCancel={closeModal}
                  ref={ref}
                />
              </LazyLoader>
            )}
          </SettingsModal>
        </Portal>
      )}
    </>
  );
};

export default ManualFitnessEntry;
